import type { Invoice, Subscription } from '../types/subscriptionManagement'
import { effectiveRenewalDate } from './subscriptionRenewal'
import { isPaidInvoiceStatus } from './subscriptionInvoiceCoverage'

function parseYmdLocal(s: string | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim())
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const dt = new Date(y, mo - 1, d)
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return null
  }
  return dt
}

/** Count FY months (Apr–Mar keys) whose calendar month overlaps [rangeStart, rangeEnd] inclusive. */
function countFyMonthsOverlapping(
  fyStart: number,
  rangeStart: Date,
  rangeEnd: Date,
): number {
  let count = 0
  for (const ym of fyMonths(fyStart)) {
    const [y, m] = ym.split('-').map(Number)
    const ms = new Date(y, m - 1, 1)
    const me = new Date(y, m, 0)
    if (me < rangeStart || ms > rangeEnd) continue
    count += 1
  }
  return count
}

/** Indian FY quarters Apr–Jun, Jul–Sep, Oct–Dec, Jan–Mar; count those overlapping the range. */
function countFyQuartersOverlapping(
  fyStart: number,
  rangeStart: Date,
  rangeEnd: Date,
): number {
  const periods: [string, string][] = [
    [`${fyStart}-04-01`, `${fyStart}-06-30`],
    [`${fyStart}-07-01`, `${fyStart}-09-30`],
    [`${fyStart}-10-01`, `${fyStart}-12-31`],
    [`${fyStart + 1}-01-01`, `${fyStart + 1}-03-31`],
  ]
  let n = 0
  for (const [a, b] of periods) {
    const qs = parseYmdLocal(a)
    const qe = parseYmdLocal(b)
    if (!qs || !qe) continue
    if (qe < rangeStart || qs > rangeEnd) continue
    n += 1
  }
  return n
}

function isPaidInvoice(inv: Invoice): boolean {
  return isPaidInvoiceStatus(inv.paymentStatus)
}

/** Count calendar months whose [1st, last] overlaps [start, end] inclusive. */
function countCalendarMonthsOverlapping(start: Date, end: Date): number {
  if (end.getTime() < start.getTime()) return 0
  let count = 0
  const cur = new Date(start.getFullYear(), start.getMonth(), 1)
  const lastMonthStart = new Date(end.getFullYear(), end.getMonth(), 1)
  while (cur.getTime() <= lastMonthStart.getTime()) {
    const y = cur.getFullYear()
    const m = cur.getMonth()
    const ms = new Date(y, m, 1)
    const me = new Date(y, m + 1, 0)
    if (!(me < start || ms > end)) count += 1
    cur.setMonth(cur.getMonth() + 1)
  }
  return count
}

/**
 * Approved budget in subscription currency from `startDate` through `throughDate`
 * (calendar months, not Indian FY):
 * - Monthly: amount × overlapping month count.
 * - Quarterly: amount × ceil(months / 3).
 * - Annual: amount × ceil(months / 12).
 */
export function approvedBudgetCapFromContractThroughDate(
  sub: Subscription,
  throughDate: Date,
): number {
  const amt = Number(sub.amount)
  if (!Number.isFinite(amt) || amt < 0) return 0
  const subStart = parseYmdLocal(sub.startDate)
  if (!subStart || subStart.getTime() > throughDate.getTime()) return 0

  const cycle = String(sub.billingCycle || '').trim().toLowerCase()
  const months = countCalendarMonthsOverlapping(subStart, throughDate)
  if (months <= 0) return 0

  if (cycle === 'annual') {
    return amt * Math.ceil(months / 12)
  }
  if (cycle === 'quarterly') {
    return amt * Math.ceil(months / 3)
  }
  return amt * months
}

/**
 * Same as {@link approvedBudgetCapFromContractThroughDate} through the latest **Paid**
 * invoice date (no “today”, no Apr–Mar FY).
 */
export function approvedBudgetCapThroughLatestPaid(
  sub: Subscription,
  invoices: Invoice[],
): number {
  let throughMs: number | null = null
  for (const inv of invoices) {
    if (!isPaidInvoice(inv)) continue
    const d = parseYmdLocal(inv.invoiceDate)
    if (!d) continue
    throughMs =
      throughMs === null ? d.getTime() : Math.max(throughMs, d.getTime())
  }
  if (throughMs === null) return 0
  return approvedBudgetCapFromContractThroughDate(sub, new Date(throughMs))
}

/**
 * Approved spend cap for an Indian FY (Apr–Mar), in subscription currency.
 * - Monthly: approved amount is per month → multiply by FY months overlapping
 *   [max(contract start, FY Apr 1), FY Mar 31].
 * - Quarterly: amount per quarter → multiply by overlapping FY quarters (max 4).
 * - Annual: amount per year → one cap for the FY if the subscription overlaps the FY.
 */
export function approvedBudgetCapForFy(
  sub: Subscription,
  fyStart: number,
): number {
  const amt = Number(sub.amount)
  if (!Number.isFinite(amt) || amt < 0) return 0

  const fyFirst = parseYmdLocal(`${fyStart}-04-01`)
  const fyLast = parseYmdLocal(`${fyStart + 1}-03-31`)
  if (!fyFirst || !fyLast) return amt

  let rangeStart = fyFirst
  const subStart = parseYmdLocal(sub.startDate)
  if (subStart && subStart.getTime() > rangeStart.getTime()) {
    rangeStart = subStart
  }
  const rangeEnd = fyLast
  if (rangeStart.getTime() > rangeEnd.getTime()) return 0

  const cycle = String(sub.billingCycle || '').trim().toLowerCase()

  if (cycle === 'annual') {
    return amt
  }
  if (cycle === 'quarterly') {
    const q = countFyQuartersOverlapping(fyStart, rangeStart, rangeEnd)
    return amt * q
  }
  // Monthly, empty, or unknown → treat as monthly
  const months = countFyMonthsOverlapping(fyStart, rangeStart, rangeEnd)
  return amt * months
}

/** Indian FY start year from today (Apr–Mar). */
export function currentFyStart(): number {
  const now = new Date()
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
}

/** YYYY-MM keys Apr(startYear) → Mar(startYear+1). */
export function fyMonths(startYear: number): string[] {
  return ['04', '05', '06', '07', '08', '09', '10', '11', '12', '01', '02', '03'].map(
    m => `${Number(m) <= 3 ? startYear + 1 : startYear}-${m}`,
  )
}

export function fyLabel(startYear: number): string {
  return `FY ${startYear}-${String(startYear + 1).slice(2)}`
}

/** YYYY-MM from `minYm` through `maxYm` inclusive (calendar months, chronological). */
export function expandCalendarMonthsInclusive(minYm: string, maxYm: string): string[] {
  const parts = (ym: string) => ym.split('-').map(Number)
  const a = parts(minYm)
  const b = parts(maxYm)
  const [y0, m0] = a
  const [y1, m1] = b
  if (!y0 || !m0 || !y1 || !m1) return []
  const out: string[] = []
  let y = y0
  let mo = m0
  for (;;) {
    out.push(`${y}-${String(mo).padStart(2, '0')}`)
    if (y === y1 && mo === m1) break
    mo += 1
    if (mo > 12) {
      mo = 1
      y += 1
    }
  }
  return out
}

/** Calendar months from earliest invoice month through latest (inclusive). */
export function monthKeysFromInvoicesAllPeriods(invoices: Invoice[]): string[] {
  const yms = new Set<string>()
  for (const i of invoices) {
    if (i.invoiceDate && i.invoiceDate.length >= 7) {
      yms.add(i.invoiceDate.slice(0, 7))
    }
    if (i.subscriptionStartDate && i.subscriptionStartDate.length >= 7) {
      yms.add(i.subscriptionStartDate.slice(0, 7))
    }
    if (i.subscriptionEndDate && i.subscriptionEndDate.length >= 7) {
      yms.add(i.subscriptionEndDate.slice(0, 7))
    }
  }
  if (yms.size === 0) return []
  const sorted = [...yms].sort()
  return expandCalendarMonthsInclusive(sorted[0], sorted[sorted.length - 1])
}

function coverageOverlapsFyWindow(
  inv: Invoice,
  winStart: string,
  winEnd: string,
): boolean {
  const covStart = inv.subscriptionStartDate?.slice(0, 10)
  const covEnd = inv.subscriptionEndDate?.slice(0, 10)
  if (!covStart || !covEnd) return false
  // subscriptionEndDate = exclusive renewal boundary
  return covStart <= winEnd && covEnd > winStart
}

export function invoiceInFy(inv: Invoice, fyStart: number): boolean {
  const months = fyMonths(fyStart)
  if (inv.invoiceDate && inv.invoiceDate.length >= 7) {
    if (months.includes(inv.invoiceDate.slice(0, 7))) return true
  }
  const winStart = `${fyStart}-04-01`
  const winEnd = `${fyStart + 1}-03-31`
  return coverageOverlapsFyWindow(inv, winStart, winEnd)
}

/**
 * Show subscription if it has an invoice in the FY, or start/renewal falls in the FY window.
 * (Previously we hid any sub that had invoices but none in the FY — that dropped rows whose
 * spend was in other years even when the contract still overlaps the selected FY.)
 */
export function subscriptionVisibleInFy(
  sub: Subscription,
  allInvoices: Invoice[],
  fyStart: number,
): boolean {
  const months = new Set(fyMonths(fyStart))
  const subInv = allInvoices.filter(i => i.subscriptionId === sub.id)
  const invInFy = subInv.some(i => invoiceInFy(i, fyStart))
  if (invInFy) return true

  const winStart = `${fyStart}-04-01`
  const winEnd = `${fyStart + 1}-03-31`
  const inWin = (d: string | undefined) => {
    if (!d || d.length < 10) return false
    const t = d.slice(0, 10)
    return t >= winStart && t <= winEnd
  }
  const renewal = effectiveRenewalDate(sub, allInvoices)
  return inWin(sub.startDate) || inWin(renewal)
}

export function availableFYsFromData(
  subscriptions: Subscription[],
  invoices: Invoice[],
): number[] {
  const years = new Set<number>()
  const addYmToFyYears = (ym: string | undefined) => {
    if (!ym || ym.length < 7) return
    const [y, m] = ym.split('-').map(Number)
    if (!y || !m) return
    years.add(m >= 4 ? y : y - 1)
  }
  invoices.forEach(inv => {
    addYmToFyYears(inv.invoiceDate?.slice(0, 7))
    addYmToFyYears(inv.subscriptionStartDate?.slice(0, 7))
    addYmToFyYears(inv.subscriptionEndDate?.slice(0, 7))
  })
  subscriptions.forEach(s => {
    const renewal = effectiveRenewalDate(s, invoices)
    for (const d of [s.startDate, renewal]) {
      if (!d || d.length < 7) continue
      const [y, m] = d.split('-').map(Number)
      if (!y || !m) continue
      years.add(m >= 4 ? y : y - 1)
    }
  })
  if (years.size === 0) years.add(currentFyStart())
  return [...years].sort((a, b) => b - a)
}
